import { bboxOfPrimaryPolygon, pixelDistance, projectedBBoxDiagonalPx, shuffle } from "./geometry.ts";
import {
  DIFFICULTY_CONFIG,
  HintExhaustedError,
  InvalidPieceStateError,
  MIN_TOLERANCE_PX,
  type GameSession,
  type HintResult,
  type PixelPoint,
  type PlacementResult,
  type Projection,
  type PuzzlePiece,
  type RegionData,
} from "./types.ts";

export interface EngineOptions {
  now?: () => number;
  rng?: () => number;
  sessionId?: string;
}

export class PuzzleEngine {
  readonly session: GameSession;
  private readonly now: () => number;

  private constructor(session: GameSession, now: () => number) {
    this.session = session;
    this.now = now;
  }

  static create(data: RegionData, options: EngineOptions = {}): PuzzleEngine {
    const now = options.now ?? Date.now;
    const rng = options.rng ?? Math.random;

    const pieces: PuzzlePiece[] = [];
    for (const country of data.countries) {
      for (const part of country.parts) {
        pieces.push({
          pieceId: part.partId,
          countryId: country.id,
          countryName: country.name,
          countryNameEn: country.nameEn ?? country.name,
          geometry: part.geometry,
          target: part.centroid,
          bbox: bboxOfPrimaryPolygon(part.geometry),
          isLocked: false,
        });
      }
    }
    if (pieces.length === 0) throw new Error("此範圍沒有任何拼圖片");

    const startedAt = now();
    return new PuzzleEngine(
      {
        sessionId: options.sessionId ?? crypto.randomUUID(),
        regionScope: data.regionScope,
        difficulty: data.difficulty,
        status: "Playing",
        pieces,
        trayOrder: shuffle(pieces.map((p) => p.pieceId), rng),
        mistakeCount: 0,
        hintUsedCount: 0,
        startedAt,
        pausedAt: null,
        accumulatedPauseMs: 0,
        elapsedSeconds: 0,
      },
      now,
    );
  }

  get config() {
    return DIFFICULTY_CONFIG[this.session.difficulty];
  }

  get lockedCount(): number {
    return this.session.pieces.reduce((n, p) => n + (p.isLocked ? 1 : 0), 0);
  }

  elapsedSeconds(): number {
    const s = this.session;
    if (s.status === "Completed") return s.elapsedSeconds;
    const end = s.pausedAt ?? this.now();
    return Math.max(0, (end - s.startedAt - s.accumulatedPauseMs) / 1000);
  }

  /** 容許誤差取「拼圖片在畫面上的外接框對角線」乘以難度比例，隨地圖縮放即時變動（SRS §7.1、§7.4） */
  allowedRadiusPx(pieceId: string, projection: Projection): number {
    const piece = this.requirePiece(pieceId);
    const diagonalPx = projectedBBoxDiagonalPx(piece.bbox, projection);
    return Math.max(diagonalPx * this.config.toleranceRatio, MIN_TOLERANCE_PX);
  }

  /**
   * 判定拖放結果。呼叫端只在「放開於地圖區域」時呼叫；放回托盤不算失誤，由 UI 自行處理。
   */
  evaluatePlacement(pieceId: string, dropPx: PixelPoint, projection: Projection): PlacementResult {
    this.requirePlaying();
    const piece = this.requirePiece(pieceId);
    if (piece.isLocked) throw new InvalidPieceStateError(`拼圖片已鎖定：${pieceId}`);

    const distancePx = pixelDistance(dropPx, projection.toPixel(piece.target));
    const allowed = this.allowedRadiusPx(pieceId, projection);
    const correct = distancePx <= allowed;

    if (correct) {
      piece.isLocked = true;
      this.session.trayOrder = this.session.trayOrder.filter((id) => id !== pieceId);
    } else {
      this.session.mistakeCount++;
    }

    const locked = this.lockedCount;
    const completed = locked === this.session.pieces.length;
    if (completed) {
      this.session.elapsedSeconds = this.elapsedSeconds();
      this.session.status = "Completed";
    }

    return {
      pieceId,
      correct,
      distancePx,
      allowedRadiusPx: allowed,
      lockedCount: locked,
      totalCount: this.session.pieces.length,
      completed,
    };
  }

  useHint(pieceId: string): HintResult {
    this.requirePlaying();
    const piece = this.requirePiece(pieceId);
    if (piece.isLocked) throw new InvalidPieceStateError(`拼圖片已鎖定：${pieceId}`);

    const { hintLimit } = this.config;
    if (this.session.hintUsedCount >= hintLimit) {
      throw new HintExhaustedError(`${this.session.difficulty} 難度的提示次數已用盡`);
    }
    this.session.hintUsedCount++;

    return {
      pieceId,
      target: piece.target,
      bbox: piece.bbox,
      geometry: piece.geometry,
      hintsRemaining: hintLimit === Infinity ? Infinity : hintLimit - this.session.hintUsedCount,
    };
  }

  pause(): void {
    this.requirePlaying();
    this.session.pausedAt = this.now();
    this.session.status = "Paused";
  }

  resume(): void {
    const s = this.session;
    if (s.status !== "Paused" || s.pausedAt === null) {
      throw new InvalidPieceStateError(`目前狀態無法繼續：${s.status}`);
    }
    s.accumulatedPauseMs += this.now() - s.pausedAt;
    s.pausedAt = null;
    s.status = "Playing";
  }

  abandon(): void {
    const s = this.session;
    if (s.status !== "Playing" && s.status !== "Paused") {
      throw new InvalidPieceStateError(`目前狀態無法放棄：${s.status}`);
    }
    s.elapsedSeconds = this.elapsedSeconds();
    s.status = "Abandoned";
  }

  private requirePlaying(): void {
    if (this.session.status !== "Playing") {
      throw new InvalidPieceStateError(`目前狀態不可操作：${this.session.status}`);
    }
  }

  private requirePiece(pieceId: string): PuzzlePiece {
    const piece = this.session.pieces.find((p) => p.pieceId === pieceId);
    if (!piece) throw new InvalidPieceStateError(`找不到拼圖片：${pieceId}`);
    return piece;
  }
}
